import { JSX, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";

import FeedbackButton from "lib/components/button/FeedbackButton";
import { Form } from "lib/components/form/FormTypes";
import Select from "lib/components/form/Select";
import TextInput from "lib/components/form/TextInput";

import { asTitle, isEmailValid } from "utils/Utils";

import { UserRole } from "organisation/OrganisationTypes";
import { Overview } from "./OrganisationUserTypes";
import useFetch from "utils/fetch/useFetch";
import OrganisationUserClient from "./OrganisationUserClient";
import AlertMessage from "lib/components/alert/AlertMessage";
import AlertBanner from "lib/components/alert/AlertBanner";

export type AddOrganisationUserDialogProps = {
	orgId: number;
	existingUsers: Array<Overview>;

	onClose: () => void;
	onCreated: (user: Overview) => void;
};

export default function AddOrganisationUserDialog(
	props: Readonly<AddOrganisationUserDialogProps>
): JSX.Element {
	const { t } = useTranslation();

	const { call } = useFetch();

	const [form, setForm] = useState<Form>({
		email: { value: "", required: true },
		role: { value: UserRole.USER, required: true }
	});

	const [alert, setAlert] = useState<AlertMessage>();

	const addOrganisationUser = useCallback((): Promise<void> => {
		closeAlert();

		form.email.error =
			!isEmailValid(form.email.value) ||
			props.existingUsers.some(user => user.email === form.email.value);

		const valid = Object.values(form).every(field => field.error !== true);
		if (!valid) {
			setForm({ ...form });
			return Promise.resolve();
		}

		return call(
			OrganisationUserClient.create(props.orgId, {
				email: form.email.value,
				role: form.role.value
			})
		)
			.then(user => props.onCreated(user))
			.catch(() => {
				setAlert(
					AlertMessage.error(t("addObjectError", { object: "user" }))
				);
			});
	}, [form]);

	const closeAlert = useCallback((): void => {
		setAlert(undefined);
	}, []);

	return (
		<Dialog open={true} maxWidth="sm" fullWidth onClose={props.onClose}>
			<DialogTitle>
				{asTitle(t("addObject", { object: "member" }))}
			</DialogTitle>
			<DialogContent>
				<Grid container direction="column" spacing={1}>
					<Grid item>
						{alert && (
							<AlertBanner alert={alert} onClose={closeAlert} />
						)}
					</Grid>
					<Grid item container alignItems="flex-end" spacing={1}>
						<Grid item xs={7} sm={8}>
							<TextInput
								label={t("email")}
								type="email"
								field={form.email}
							/>
						</Grid>
						<Grid item xs={5} sm={4}>
							<Select
								label={t("role")}
								field={form.role}
								options={Object.values(UserRole).map(role => [
									role,
									t(role)
								])}
							/>
						</Grid>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={props.onClose}>
					{asTitle(t("cancel"))}
				</Button>
				<FeedbackButton
					variant="contained"
					label={asTitle(t("add"))}
					onClick={addOrganisationUser}
				/>
			</DialogActions>
		</Dialog>
	);
}
